/**
 * Chunk loading error handler
 * Handles chunk loading failures and provides retry mechanisms
 */

interface ChunkLoadError extends Error {
  type?: string;
  chunk?: string;
  request?: string;
}

/**
 * Check if an error is a chunk loading error
 */
export function isChunkLoadError(error: unknown): error is ChunkLoadError {
  if (!error || typeof error !== "object") return false;
  const err = error as ChunkLoadError;
  return (
    err.name === "ChunkLoadError" ||
    err.message?.includes("Loading chunk") ||
    err.message?.includes("Failed to fetch dynamically imported module") ||
    err.type === "chunkLoadError"
  );
}

/**
 * Retry loading a chunk
 */
export async function retryChunkLoad(
  chunkLoader: () => Promise<unknown>,
  maxRetries = 3,
  delay = 1000
): Promise<unknown> {
  let lastError: unknown;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await chunkLoader();
    } catch (error) {
      lastError = error;
      if (attempt < maxRetries - 1) {
        // Exponential backoff
        await new Promise((resolve) =>
          setTimeout(resolve, delay * Math.pow(2, attempt))
        );
      }
    }
  }

  throw lastError;
}

/**
 * Handle chunk load error globally
 */
export function handleChunkLoadError(error: ChunkLoadError) {
  console.warn("Chunk load error detected:", error);

  // Try to reload the page if it's a chunk load error
  if (typeof window !== "undefined" && isChunkLoadError(error)) {
    // Only reload if we haven't reloaded recently (prevent infinite loop)
    let shouldReload = true;
    const now = Date.now();

    // Safely read from sessionStorage
    try {
      if (window.sessionStorage) {
        const lastReload = window.sessionStorage.getItem("lastChunkReload");
        if (lastReload) {
          const lastReloadTime = Number(lastReload);
          // Validate parsed value is a finite number
          if (Number.isFinite(lastReloadTime)) {
            const timeSinceLastReload = now - lastReloadTime;
            // Only reload if more than 30 seconds have passed
            if (timeSinceLastReload <= 30000) {
              shouldReload = false;
            }
          }
        }
      }
    } catch (error) {
      // If storage access fails, proceed with reload attempt
      // (better to try reloading than to fail silently)
      console.warn(
        "Failed to read from sessionStorage, proceeding with reload:",
        error
      );
    }

    if (shouldReload) {
      // Safely write to sessionStorage (don't block reload if it fails)
      try {
        if (window.sessionStorage) {
          window.sessionStorage.setItem("lastChunkReload", now.toString());
        }
      } catch (error) {
        // If storage write fails, still proceed with reload
        console.warn(
          "Failed to write to sessionStorage, proceeding with reload:",
          error
        );
      }

      console.log("Attempting to reload page due to chunk load error...");
      window.location.reload();
      return;
    }
  }
}

/**
 * Setup global chunk error handler
 */
export function setupChunkErrorHandler() {
  if (typeof window === "undefined") return;

  // Handle unhandled chunk load errors
  window.addEventListener("error", (event) => {
    if (isChunkLoadError(event.error)) {
      event.preventDefault();
      handleChunkLoadError(event.error);
    }
  });

  // Handle unhandled promise rejections (common for dynamic imports)
  window.addEventListener("unhandledrejection", (event) => {
    if (isChunkLoadError(event.reason)) {
      event.preventDefault();
      handleChunkLoadError(event.reason);
    }
  });
}
